// Import escapeHTML
import { escapeHTML } from '@wordpress/escape-html';

// Import i18n functions
import { __ } from '@wordpress/i18n';

// Import builderFetch
import { builderFetch } from '../../utils';

/**
 * Reloads the current recipe data from the server.
 * 
 * This service fetches the latest recipe data and builderFetch automatically updates the global UncannyAutomator._recipe object and dispatches UI update events.
 * 
 * @param {Object} options - Configuration options
 * @param {Number} [options.recipeId] - The recipe ID to reload (defaults to current recipe)
 * @param {Boolean} [options.showSnackbar=false] - Whether to show loading/success snackbars
 * @return {Promise} Promise that resolves with the API response
 * 
 * @example
 * // Reload current recipe silently
 * await reloadRecipe();
 * 
 * @example
 * // Reload with notifications
 * await reloadRecipe({ showSnackbar: true });
 * 
 * @example
 * // Reload specific recipe
 * await reloadRecipe({ recipeId: 123, showSnackbar: true });
 */
const reloadRecipe = async ( { recipeId, showSnackbar = true } = {} ) => {
	// Event name
	const eventName = 'uap/builder/core/reload-recipe';

	// Use provided recipe ID or fall back to current recipe
	const targetRecipeId = recipeId || UncannyAutomator._recipe?.recipe_id;

	// Validate we have a recipe ID
	if ( ! targetRecipeId ) {
		console.error( 'reloadRecipe: No recipe ID provided and no current recipe found' );
		return Promise.reject( new Error( 'No recipe ID available' ) );
	}

	// Build endpoint with query parameter
	const endpoint = `get_recipe/?recipe_id=${ targetRecipeId }`;

	// Fetch the recipe
	// builderFetch will automatically update UncannyAutomator._recipe and dispatch events
	return builderFetch( {
		eventName: eventName,
		endpoint: endpoint,
		method: 'GET',
		snackbar: showSnackbar ? {
			loading: escapeHTML( __( 'Reloading recipe...', 'uncanny-automator' ) ),
			success: escapeHTML( __( 'Recipe reloaded successfully', 'uncanny-automator' ) ),
		} : {
			loading: '',
			success: '',
		}
	} ).then( response => {
		// Check if we got a response
		if ( ! response ) {
			throw new Error( 'Failed to reload recipe: No response from server' );
		}

		// Check if the call was successful
		if ( response.success ) {
			// Dispatch custom event
			document.dispatchEvent( new CustomEvent( eventName, {
				detail: {
					recipeId: targetRecipeId,
				}
			} ) );
		} else {
			// Throw error if not successful
			throw new Error( response.message || 'Failed to reload recipe' );
		}

		return response;
	} );
};

/**
 * Initialize event listener for global reload-recipe events.
 * This allows any part of the application to trigger a recipe reload by dispatching:
 * 
 * document.dispatchEvent(new CustomEvent('uap/builder/reload-recipe', {
 *     detail: { recipeId: 123, showSnackbar: true }
 * }));
 */
if ( typeof document !== 'undefined' ) {
	document.addEventListener( 'uap.builder.recipe.reload', ( event ) => {
		const { recipeId, showSnackbar } = event.detail || {};
		reloadRecipe( { recipeId, showSnackbar } ).catch( error => {
			console.error( 'Failed to reload recipe via event:', error );
		} );
	} );
}

export default reloadRecipe;

